import * as React from 'react';
import { styled } from '@mui/material/styles';
import Divider, { dividerClasses } from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { paperClasses } from '@mui/material/Paper';
import { listClasses } from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import MenuButton from './MenuButton';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { clearUser } from '../redux/authSlice';
import config from '../config';
import { useAuth } from "react-oidc-context";
import { setAccessToken } from '../utils/axiosInstance';

const MenuItem = styled(MuiMenuItem)({
  margin: '2px 0',
});

function getDiscoveryUrl(cognitoAuthConfig) {
  if (cognitoAuthConfig.metadataUrl) {
    return cognitoAuthConfig.metadataUrl;
  }

  const baseAuthority = (cognitoAuthConfig.authority || '').replace(/\/$/, '');
  if (!baseAuthority) {
    return '';
  }

  return `${baseAuthority}/.well-known/openid-configuration`;
}

async function resolveLogoutBaseUrl(cognitoAuthConfig) {
  const discoveryUrl = getDiscoveryUrl(cognitoAuthConfig);
  if (!discoveryUrl) {
    return '';
  }

  const response = await fetch(discoveryUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Unable to load OIDC metadata (${response.status})`);
  }

  const metadata = await response.json();
  const authorizationEndpoint = metadata?.authorization_endpoint || metadata?.end_session_endpoint;
  if (!authorizationEndpoint) {
    throw new Error('OIDC metadata did not include an authorization endpoint');
  }

  return new URL(authorizationEndpoint).origin;
}

export default function OptionsMenu() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const auth = useAuth();
  const { cognitoAuthConfig } = config;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMyAccount = () => {
    navigate('/my-account');
  };

  const handleLogout = async () => {
    handleClose();
    dispatch(clearUser());
    setAccessToken(null);

    try {
      await auth.removeUser();
    } catch (error) {
      console.error('Failed to clear local auth session during logout', error);
    }

    sessionStorage.setItem("justLoggedOut", "true");

    try {
      const logoutBaseUrl = await resolveLogoutBaseUrl(cognitoAuthConfig);
      const logoutUrl =
        `${logoutBaseUrl}/logout` +
        `?client_id=${encodeURIComponent(cognitoAuthConfig.client_id)}` +
        `&logout_uri=${encodeURIComponent(cognitoAuthConfig.post_logout_redirect_uri)}`;

      window.location.replace(logoutUrl);
      return;
    } catch (error) {
      console.error('Failed to resolve Cognito logout endpoint, falling back to local sign-out only', error);
    }

    navigate('/login', { replace: true });
  };

  return (
    <React.Fragment>
      <MenuButton
        aria-label="Open menu"
        onClick={handleClick}
        sx={{ borderColor: 'transparent' }}
      >
        <MoreVertRoundedIcon />
      </MenuButton>
      <Menu
        anchorEl={anchorEl}
        id="menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          [`& .${listClasses.root}`]: {
            padding: '4px',
          },
          [`& .${paperClasses.root}`]: {
            padding: 0,
          },
          [`& .${dividerClasses.root}`]: {
            margin: '4px -4px',
          },
        }}
      >
        <MenuItem
          onClick={handleMyAccount}
          sx={{
            [`& .${listItemIconClasses.root}`]: {
              ml: 'auto',
              minWidth: 0,
            },
          }}
        >
          <ListItemText>My account</ListItemText>
          <ListItemIcon>
            <AccountCircleRoundedIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            [`& .${listItemIconClasses.root}`]: {
              ml: 'auto',
              minWidth: 0,
            },
          }}
        >
          <ListItemText>Logout</ListItemText>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
